import * as React from "react";
import { Boxes, Camera } from "lucide-react";
import { GoalItemsPanel } from "../GoalItemsPanel.js";
import { PlacementEngine } from "../PlacementEngine.js";
import { ReorganizeFromToModal } from "../ReorganizeFromToModal.js";
import { ScanToItemsModal } from "../ScanToItemsModal.js";
import { useToast } from "@/components/Toast.js";
import { useFeatureFlags } from "@/contexts/FeatureFlagContext.js";
import { apiFetch } from "@/lib/api.js";
import type { GoalDetail } from "../types.js";

/**
 * ReorganizeGoalView — detail body for a "reorganize a space" goal.
 *
 * Reorganize and Find-a-home share the exact same placement machinery, so this
 * view composes the same shared PlacementEngine + items panel. (A later PR wires
 * the space-page ReorganizeContainersPanel onto this same engine so "reorganize
 * from a goal" and "reorganize from a space" are one implementation.)
 */
export function ReorganizeGoalView({
  goal,
  goalId,
  isOwner,
  onSetGoal,
  refetchGoal,
}: {
  goal: GoalDetail;
  goalId: string;
  isOwner: boolean;
  onSetGoal: (goal: GoalDetail) => void;
  refetchGoal: () => Promise<void> | void;
}) {
  const toast = useToast();
  const { aiEnabled } = useFeatureFlags();
  const [showLinkItems, setShowLinkItems] = React.useState(false);
  const [showFromTo, setShowFromTo] = React.useState(false);
  const [showScan, setShowScan] = React.useState(false);

  return (
    <>
      {/* Reorganize actions (F-e): a bulk from→to container path and a
          "scan this space" path, both complementing the item-by-item engine. */}
      {isOwner && goal.status === "active" && (
        <div className="space-y-2">
          <button
            onClick={() => setShowFromTo(true)}
            className="w-full flex items-center gap-3 p-4 rounded-lg border border-clay-border bg-white hover:border-primary/40 transition-colors text-left"
          >
            <div className="w-10 h-10 rounded-md bg-amethyst-soft flex items-center justify-center shrink-0">
              <Boxes className="w-5 h-5 text-primary" />
            </div>
            <div>
              <div className="text-[1.0625rem] font-semibold text-ink">Reorganize by container</div>
              <div className="text-[0.8125rem] text-faint">Move everything from one set of containers into another, then try it for two weeks.</div>
            </div>
          </button>
          {aiEnabled && (
            <button
              onClick={() => setShowScan(true)}
              className="w-full flex items-center gap-3 p-4 rounded-lg border border-clay-border bg-white hover:border-sage-deep/40 transition-colors text-left"
            >
              <div className="w-10 h-10 rounded-md bg-sage-wash flex items-center justify-center shrink-0">
                <Camera className="w-5 h-5 text-sage-deep" />
              </div>
              <div>
                <div className="text-[1.0625rem] font-semibold text-ink">Scan this space</div>
                <div className="text-[0.8125rem] text-faint">Photograph the space to detect what's in it and add those items to reorganize.</div>
              </div>
            </button>
          )}
        </div>
      )}

      <PlacementEngine
        goal={goal}
        goalId={goalId}
        isOwner={isOwner}
        refetchGoal={refetchGoal}
        onAddItems={() => setShowLinkItems(true)}
        itemsSlot={
          <GoalItemsPanel
            goal={goal}
            goalId={goalId}
            isOwner={isOwner}
            onSetGoal={onSetGoal}
            onItemsChanged={refetchGoal}
            showLinkItems={showLinkItems}
            setShowLinkItems={setShowLinkItems}
          />
        }
      />

      <ReorganizeFromToModal
        open={showFromTo}
        onClose={() => setShowFromTo(false)}
        goalId={goalId}
        goalSpaceId={goal.activityLocationId}
        onCreated={refetchGoal}
      />

      {/* Scan this space (F-e §3.2) — reuse the shared scan primitive to detect
          items in the space (create-or-match into it) and link them to the goal
          so there's something to reorganize. */}
      <ScanToItemsModal
        open={showScan}
        onClose={() => setShowScan(false)}
        targetSpaceId={goal.activityLocationId}
        onResolved={async (itemIds) => {
          try {
            if (itemIds.length > 0) {
              await apiFetch(`/api/organizing-goals/${goalId}/items`, {
                method: "POST",
                body: JSON.stringify({ itemIds }),
              });
            }
            toast.show(`${itemIds.length} item(s) added to reorganize`, "success");
            await refetchGoal();
          } catch (err: any) {
            toast.show(err.message || "Added to the space, but couldn't link to the goal", "error");
          }
        }}
        title="Scan this space"
      />
    </>
  );
}
